import { useUIStateStore } from '../../../application/store/UIStateStore';
import { ProductFilter } from '@domain/product/models/ProductFilter';
import { useEffect, useRef, useState } from 'react';

/**
 * A hook for filtering products based on criteria with global state persistence.
 * Also owns page-level pagination state, resetting to page 1 whenever the filter
 * content (excluding page/limit) actually changes — comparing serialized content
 * rather than watching `filterCriteria.page`, since the store resolves that to 1
 * on every filter change and a value-based effect would only fire once.
 * @returns Object containing filtered products, filter criteria, pagination state, and methods to update filters
 */
export const useProductsFilter = (defaultLimit = 50) => {
  const { filters, setFilter, clearFilters } = useUIStateStore();

  const filterCriteria = filters.products as ProductFilter;

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimit);

  const { page: _page, limit: _limit, ...nonPageCriteria } = filterCriteria;
  const nonPageCriteriaKey = JSON.stringify(nonPageCriteria);
  const prevKeyRef = useRef(nonPageCriteriaKey);

  useEffect(() => {
    if (prevKeyRef.current !== nonPageCriteriaKey) {
      prevKeyRef.current = nonPageCriteriaKey;
      setPage(1);
    }
  }, [nonPageCriteriaKey]);

  // Migrate legacy persisted single collectionId → collectionIds (runs after persist rehydrate)
  useEffect(() => {
    const raw = filters.products as Record<string, unknown>;
    const legacyId = raw.collectionId;
    if (typeof legacyId === 'string' && legacyId) {
      const hasNew = Array.isArray(raw.collectionIds) && raw.collectionIds.length > 0;
      if (!hasNew) {
        setFilter('products', 'collectionIds', [legacyId]);
      }
      setFilter('products', 'collectionId', undefined);
    }
  }, [filters.products, setFilter]);

  // Remove the active filter since where no longer using it this way
  useEffect(() => {
    if (filterCriteria.active !== undefined) {
      setFilter('products', 'active', undefined);
    }
  }, [filterCriteria]);

  const updateFilter = (criteria: ProductFilter) => {
    const { page: nextPage, ...nextRest } = criteria;
    const { page: currentPage, ...currentRest } = filterCriteria;

    // Any change outside of `page` itself should reset pagination to page 1,
    // unless this call is only changing the page (e.g. clicking pagination controls).
    const nonPageFieldsChanged = JSON.stringify(nextRest) !== JSON.stringify(currentRest);
    const resolvedPage = nonPageFieldsChanged ? 1 : nextPage;

    const resolvedCriteria: ProductFilter = { ...criteria, page: resolvedPage };

    // Update each filter property individually to trigger proper state updates
    Object.keys(resolvedCriteria).forEach(key => {
      setFilter('products', key, resolvedCriteria[key as keyof ProductFilter]);
    });

    // Clear any filters that are not in the new criteria
    Object.keys(filterCriteria).forEach(key => {
      if (!(key in resolvedCriteria)) {
        setFilter('products', key, undefined);
      }
    });
  };

  const clearAllFilters = () => {
    clearFilters('products');
  };

  return {
    filterCriteria,
    updateFilter,
    clearFilters: clearAllFilters,
    page,
    setPage,
    limit,
    setLimit,
  };
};
